import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Users,
  FileText,
  CalendarDays,
  AlertTriangle,
  TrendingUp,
  Clock,
  LogIn,
} from 'lucide-react';
import { format } from 'date-fns';
import { sq } from 'date-fns/locale';
import clsx from 'clsx';
import { dashboardApi, postsApi, usersApi, activityApi, type IActivityUserGroup } from '../services/api';
import type { IPost } from '@barrikadave/shared';
import Badge, { getPriorityBadgeVariant, getRoleBadgeVariant } from '../components/Badge';
import LoadingSpinner from '../components/LoadingSpinner';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  loading?: boolean;
  to?: string;
}

function StatCard({ title, value, icon: Icon, iconBg, iconColor, loading, to }: StatCardProps) {
  const content = (
    <>
      <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center shrink-0', iconBg)}>
        <Icon className={clsx('h-6 w-6', iconColor)} />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500">{title}</p>
        {loading ? (
          <LoadingSpinner size="sm" className="mt-1" />
        ) : (
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        )}
      </div>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="card flex items-center gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
      >
        {content}
      </Link>
    );
  }

  return <div className="card flex items-center gap-4">{content}</div>;
}

function RecentPostCard({ post }: { post: IPost }) {
  const authorName = `${post.author.firstName} ${post.author.lastName}`;
  const displayDate = format(new Date(post.publishedAt || post.createdAt), 'dd MMM, HH:mm', {
    locale: sq,
  });

  return (
    <Link
      to={`/posts?postId=${post.id}`}
      className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <div className="w-8 h-8 rounded-full bg-barrikadave-100 flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-barrikadave-600 font-semibold text-xs">
          {post.author.firstName[0]}
          {post.author.lastName[0]}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-gray-900 truncate">
            {post.title || post.content?.slice(0, 60) || 'Pa titull'}
          </p>
          <Badge variant={getPriorityBadgeVariant(post.priority)}>{post.priority}</Badge>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-500">{authorName}</span>
          <span className="text-xs text-gray-300">|</span>
          <span className="text-xs text-gray-400">{displayDate}</span>
        </div>
      </div>
    </Link>
  );
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  LOGIN: { label: 'Hyrje', color: 'bg-green-100 text-green-700' },
  LOGOUT: { label: 'Dalje', color: 'bg-gray-100 text-gray-700' },
  POST_VIEW: { label: 'Shikoi postim', color: 'bg-blue-100 text-blue-700' },
  POST_CREATE: { label: 'Krijoi postim', color: 'bg-purple-100 text-purple-700' },
  POST_EDIT: { label: 'Ndryshoi postim', color: 'bg-yellow-100 text-yellow-700' },
  POST_DELETE: { label: 'Fshiu postim', color: 'bg-red-100 text-red-700' },
  SCREENSHOT_ATTEMPT: { label: 'Screenshot', color: 'bg-red-100 text-red-700' },
  APP_OPEN: { label: 'Hapi app', color: 'bg-green-100 text-green-700' },
  APP_CLOSE: { label: 'Mbylli app', color: 'bg-gray-100 text-gray-700' },
  APP_BACKGROUND: { label: 'App background', color: 'bg-gray-100 text-gray-700' },
  SCREEN_VIEW: { label: 'Shikoi ekran', color: 'bg-blue-100 text-blue-700' },
  CLIPBOARD_COPY: { label: 'Kopjoi', color: 'bg-orange-100 text-orange-700' },
  FILE_DOWNLOAD: { label: 'Shkarkoi file', color: 'bg-indigo-100 text-indigo-700' },
  NOTIFICATION_OPEN: { label: 'Hapi njoftim', color: 'bg-cyan-100 text-cyan-700' },
};

const POST_RELATED_ACTIONS = new Set([
  'POST_VIEW',
  'POST_CREATE',
  'POST_EDIT',
  'POST_DELETE',
  'NOTIFICATION_OPEN',
]);

function RecentAccessRow({ group }: { group: IActivityUserGroup }) {
  const { user, lastEvent } = group;
  const firstName = user?.firstName ?? '';
  const lastName = user?.lastName ?? '';
  const role = user?.role ?? '';
  const initials = `${firstName[0] ?? '?'}${lastName[0] ?? ''}`;
  const displayDate = format(new Date(lastEvent.createdAt), 'dd MMM, HH:mm', { locale: sq });
  const actionInfo = ACTION_LABELS[lastEvent.action] || {
    label: lastEvent.action,
    color: 'bg-gray-100 text-gray-700',
  };
  const meta = lastEvent.metadata as Record<string, any> | null | undefined;
  const postId = meta?.postId as string | undefined;
  const postTitle = meta?.postTitle as string | undefined;
  const showPostLink = !!postId && POST_RELATED_ACTIONS.has(lastEvent.action);

  return (
    <Link
      to={`/activity/${user.id}`}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
    >
      <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
        <span className="text-sky-600 font-semibold text-xs">{initials || '?'}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-gray-900 truncate">
            {firstName || lastName ? `${firstName} ${lastName}` : '—'}
          </p>
          {role && <Badge variant={getRoleBadgeVariant(role)}>{role}</Badge>}
          <span
            className={clsx(
              'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium',
              actionInfo.color,
            )}
          >
            {actionInfo.label}
          </span>
          <span className="ml-auto text-[11px] text-gray-400">{group.totalEvents} veprime</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 min-w-0">
          <span className="text-xs text-gray-400 shrink-0">{displayDate}</span>
          {showPostLink && (
            <>
              <span className="text-xs text-gray-300">|</span>
              <Link
                to={`/posts?postId=${postId}`}
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-barrikadave-600 hover:underline truncate"
                title={postTitle || postId}
              >
                {postTitle || 'Pa titull'}
              </Link>
            </>
          )}
          {!showPostLink && lastEvent.ipAddress && (
            <>
              <span className="text-xs text-gray-300">|</span>
              <span className="text-xs text-gray-400 font-mono">{lastEvent.ipAddress}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function DashboardPage() {
  const statsQuery = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardApi.getStats,
    refetchInterval: 30000,
    retry: 1,
  });

  const recentPostsQuery = useQuery({
    queryKey: ['recent-posts'],
    queryFn: () => postsApi.getAll({ limit: 5, sort: 'latest' }),
    refetchInterval: 30000,
    retry: 1,
  });

  const topUsersQuery = useQuery({
    queryKey: ['users-list-brief'],
    queryFn: () => usersApi.getAll({ limit: 5 }),
    refetchInterval: 30000,
    retry: 1,
  });

  const recentAccessQuery = useQuery({
    queryKey: ['recent-access-grouped'],
    queryFn: () => activityApi.getUsersGrouped({ limit: 8 }),
    refetchInterval: 30000,
    retry: 1,
  });

  const stats = statsQuery.data;
  const recentPosts = recentPostsQuery.data?.data ?? [];
  const topUsers = topUsersQuery.data?.data ?? [];

  const totalUsers = stats?.totalUsers ?? topUsersQuery.data?.pagination?.total ?? 0;
  const totalPosts = stats?.totalPosts ?? recentPostsQuery.data?.pagination?.total ?? 0;
  const postsToday = stats?.postsToday ?? 0;
  const urgentPosts = stats?.urgentPosts ?? 0;

  const isStatsLoading = statsQuery.isLoading;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Perdorues Gjithsej"
          value={totalUsers}
          icon={Users}
          iconBg="bg-barrikadave-100"
          iconColor="text-barrikadave-600"
          loading={isStatsLoading}
          to="/users"
        />
        <StatCard
          title="Postime Gjithsej"
          value={totalPosts}
          icon={FileText}
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
          loading={isStatsLoading}
          to="/posts"
        />
        <StatCard
          title="Postime Sot"
          value={postsToday}
          icon={CalendarDays}
          iconBg="bg-sky-100"
          iconColor="text-sky-600"
          loading={isStatsLoading}
          to="/posts"
        />
        <StatCard
          title="Postime Urgjente"
          value={urgentPosts}
          icon={AlertTriangle}
          iconBg="bg-red-100"
          iconColor="text-red-600"
          loading={isStatsLoading}
          to="/posts?priority=URGENT"
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Posts */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-gray-400" />
            <h3 className="text-base font-semibold text-gray-900">Postimet e Fundit</h3>
          </div>
          {recentPostsQuery.isLoading ? (
            <LoadingSpinner className="py-8" />
          ) : recentPosts.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nuk ka postime per te shfaqur</p>
          ) : (
            <div className="space-y-1 -mx-3">
              {recentPosts.map((post) => (
                <RecentPostCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>

        {/* Top Authors */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-gray-400" />
            <h3 className="text-base font-semibold text-gray-900">Autoret Kryesore</h3>
          </div>
          {topUsersQuery.isLoading ? (
            <LoadingSpinner className="py-8" />
          ) : topUsers.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nuk ka te dhena</p>
          ) : (
            <div className="space-y-3">
              {(stats?.topAuthors ?? topUsers).slice(0, 5).map((author, index) => {
                const firstName = 'firstName' in author ? author.firstName : '';
                const lastName = 'lastName' in author ? author.lastName : '';
                const role = 'role' in author ? author.role : '';
                const postCount = 'postCount' in author ? (author as { postCount: number }).postCount : 0;

                return (
                  <div key={author.id} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                      <span className="text-gray-600 font-semibold text-xs">{index + 1}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {firstName} {lastName}
                      </p>
                      <Badge variant={getRoleBadgeVariant(role)}>{role}</Badge>
                    </div>
                    {postCount > 0 && (
                      <span className="text-sm font-semibold text-barrikadave-600">
                        {postCount}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Access from Registry */}
      {!recentAccessQuery.isError && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <LogIn className="h-5 w-5 text-gray-400" />
            <h3 className="text-base font-semibold text-gray-900">Aksesimet e Fundit</h3>
          </div>
          {recentAccessQuery.isLoading ? (
            <LoadingSpinner className="py-8" />
          ) : (recentAccessQuery.data?.data ?? []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nuk ka aksesime te regjistruara</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 -mx-3">
              {(recentAccessQuery.data?.data ?? []).map((group) => (
                <RecentAccessRow key={group.user.id} group={group} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
